import {
  type ColumnDef,
  type ExpandedState,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type PaginationState,
  type Row,
  type RowSelectionState,
  type SortingState,
  type Table,
  type TableOptions,
  type Updater,
  useVueTable,
} from '@tanstack/vue-table';
import { computed, inject, type MaybeRef, type Ref, ref, toValue, watch } from 'vue';

import type { DataTableColumnUiState } from '../../components/DataTable/DataTableColumnUi.types';
import { DATA_VIEW_INJECTION } from '../../components/DataView/DataView.keys';
import type { DataViewInjection } from '../../components/DataView/DataView.types';

interface UseDataTableOptions<TData> {
  columns: MaybeRef<ColumnDef<TData, unknown>[]>;
  data: MaybeRef<TData[]>;
  /**
   * TanStack initialState for sorting, pagination, selection, expansion, pinning, column order/sizing, etc.
   * **`columnVisibility`** and **`columnOrder`** are stripped before `useVueTable` when using **`columnUi`**; set them with **`v-model:column-ui`** instead.
   */
  initialState?: TableOptions<TData>['initialState'];
  /**
   * Column visibility and order. Pass a ref (e.g. from DataTable `v-model:column-ui`). When omitted, an internal empty ref is used.
   */
  columnUi?: Ref<DataTableColumnUiState>;
  enableSorting?: boolean;
  enableFiltering?: boolean;
  enablePagination?: boolean;
  pageSize?: number;
  getSubRows?: (row: TData) => TData[] | undefined;
  getRowCanExpand?: (row: Row<TData>) => boolean;
  getRowId?: (row: TData) => string;
  enableRowSelection?: (row: Row<TData>) => boolean;
}

interface UseDataTableReturn<TData> {
  table: Table<TData>;
  isServerMode: boolean;
}

function isUpdaterFunction<T>(updater: Updater<T>): updater is (old: T) => T {
  return typeof updater === 'function';
}

function applyUpdater<T>(updater: Updater<T>, old: T): T {
  return isUpdaterFunction(updater) ? updater(old) : updater;
}

/** Omit `columnVisibility` + `columnOrder` so they are only driven by `columnUi` (TanStack controlled state). */
function tableInitialStateWithoutColumnUiControlledKeys<TData>(
  initialState: TableOptions<TData>['initialState'] | undefined,
): TableOptions<TData>['initialState'] | undefined {
  if (initialState == null) return undefined;
  const rest = { ...initialState };
  delete rest.columnVisibility;
  delete rest.columnOrder;
  return Object.keys(rest).length > 0 ? rest : undefined;
}

export default function useDataTable<TData>({
  columns,
  data,
  initialState,
  columnUi: columnUiOption,
  enableSorting = true,
  enableFiltering = true,
  enablePagination = true,
  pageSize: defaultPageSize = 10,
  getSubRows,
  getRowCanExpand,
  getRowId,
  enableRowSelection,
}: UseDataTableOptions<TData>): UseDataTableReturn<TData> {
  const dataView = inject(DATA_VIEW_INJECTION.key, DATA_VIEW_INJECTION.defaults);

  // If dataview injection is present, we are in server mode and set up vue-table to let
  // dataview handle pagination, sorting, filtering, etc.
  const isServerMode = dataView.variant?.value === 'table';

  if (isServerMode) {
    return createServerTable({
      columns,
      data,
      initialState,
      columnUiOption,
      enableSorting,
      dataView,
      getSubRows,
      getRowCanExpand,
      getRowId,
      enableRowSelection,
    });
  }

  return createClientTable({
    columns,
    data,
    initialState,
    columnUiOption,
    enableSorting,
    enableFiltering,
    enablePagination,
    pageSize: defaultPageSize,
    getSubRows,
    getRowCanExpand,
    getRowId,
    enableRowSelection,
  });
}

function createServerTable<TData>({
  columns,
  data,
  initialState,
  columnUiOption,
  enableSorting,
  dataView,
  getSubRows,
  getRowCanExpand,
  getRowId,
  enableRowSelection,
}: {
  columns: MaybeRef<ColumnDef<TData, unknown>[]>;
  data: MaybeRef<TData[]>;
  initialState?: TableOptions<TData>['initialState'];
  columnUiOption?: Ref<DataTableColumnUiState>;
  enableSorting: boolean;
  dataView: DataViewInjection;
  getSubRows?: (row: TData) => TData[] | undefined;
  getRowCanExpand?: (row: Row<TData>) => boolean;
  getRowId?: (row: TData) => string;
  enableRowSelection?: (row: Row<TData>) => boolean;
}): UseDataTableReturn<TData> {
  const sorting = computed<SortingState>(() => {
    const id = dataView.currentSortId.value;
    const order = dataView.currentSortOrder.value;
    if (!id) return [];
    return [{ id, desc: order === 'desc' }];
  });

  const pagination = computed<PaginationState>(() => ({
    pageIndex: dataView.currentPage.value - 1,
    pageSize: dataView.pageSize.value,
  }));

  const rowSelection = ref<RowSelectionState>(initialState?.rowSelection ?? {});
  const expanded = ref<ExpandedState>(initialState?.expanded ?? {});
  const columnUi = columnUiOption ?? ref<DataTableColumnUiState>({});

  const table = useVueTable({
    get columns() {
      return toValue(columns);
    },
    get data() {
      return toValue(data);
    },
    initialState: tableInitialStateWithoutColumnUiControlledKeys(initialState),
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    paginateExpandedRows: false,
    manualSorting: true,
    manualPagination: true,
    manualFiltering: true,
    enableColumnPinning: true,
    enableSorting,
    getSubRows,
    getRowCanExpand,
    ...(getRowId != null ? { getRowId } : {}),
    ...(enableRowSelection != null ? { enableRowSelection } : {}),
    state: {
      get sorting() {
        return sorting.value;
      },
      get pagination() {
        return pagination.value;
      },
      get rowSelection() {
        return rowSelection.value;
      },
      get expanded() {
        return expanded.value;
      },
      get columnVisibility() {
        return columnUi.value.columnVisibility ?? {};
      },
      get columnOrder() {
        const next = columnUi.value.columnOrder;
        return next ?? [];
      },
    },
    get rowCount() {
      const total = dataView.totalDataCount.value;
      return total === Infinity ? -1 : total;
    },
    onSortingChange(updater) {
      const next = applyUpdater(updater, sorting.value);
      const first = next[0];
      if (!first) {
        dataView.updateCurrentSort(undefined, { shouldEmit: true });
      } else {
        dataView.updateCurrentSort(first.id, {
          sortOrder: first.desc ? 'desc' : 'asc',
          shouldEmit: true,
        });
      }
    },
    onPaginationChange(updater) {
      const next = applyUpdater(updater, pagination.value);
      dataView.setPage(next.pageIndex + 1, { shouldEmit: true });
    },
    onRowSelectionChange(updater) {
      rowSelection.value = applyUpdater(updater, rowSelection.value);
    },
    onExpandedChange(updater) {
      expanded.value = applyUpdater(updater, expanded.value);
    },
    onColumnVisibilityChange(updater) {
      const prev = columnUi.value.columnVisibility ?? {};
      const next = applyUpdater(updater, prev);
      columnUi.value = { ...columnUi.value, columnVisibility: next };
    },
    onColumnOrderChange(updater) {
      const prev = columnUi.value.columnOrder ?? [];
      const next = applyUpdater(updater, prev);
      columnUi.value = { ...columnUi.value, columnOrder: next };
    },
  });

  return { table, isServerMode: true };
}

function createClientTable<TData>({
  columns,
  data,
  initialState,
  columnUiOption,
  enableSorting,
  enableFiltering,
  enablePagination,
  pageSize,
  getSubRows,
  getRowCanExpand,
  getRowId,
  enableRowSelection,
}: {
  columns: MaybeRef<ColumnDef<TData, unknown>[]>;
  data: MaybeRef<TData[]>;
  initialState?: TableOptions<TData>['initialState'];
  columnUiOption?: Ref<DataTableColumnUiState>;
  enableSorting: boolean;
  enableFiltering: boolean;
  enablePagination: boolean;
  pageSize: number;
  getSubRows?: (row: TData) => TData[] | undefined;
  getRowCanExpand?: (row: Row<TData>) => boolean;
  getRowId?: (row: TData) => string;
  enableRowSelection?: (row: Row<TData>) => boolean;
}): UseDataTableReturn<TData> {
  const sorting = ref<SortingState>(initialState?.sorting || []);
  const paginationState = ref<PaginationState>({
    pageIndex: initialState?.pagination?.pageIndex || 0,
    pageSize: initialState?.pagination?.pageSize || pageSize,
  });
  const rowSelection = ref<RowSelectionState>(initialState?.rowSelection || {});
  const expanded = ref<ExpandedState>(initialState?.expanded ?? {});
  const columnUi = columnUiOption ?? ref<DataTableColumnUiState>({});

  const table = useVueTable({
    get columns() {
      return toValue(columns);
    },
    get data() {
      return toValue(data);
    },
    initialState: tableInitialStateWithoutColumnUiControlledKeys(initialState),
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    paginateExpandedRows: false,
    ...(enableSorting ? { getSortedRowModel: getSortedRowModel() } : {}),
    ...(enableFiltering ? { getFilteredRowModel: getFilteredRowModel() } : {}),
    ...(enablePagination ? { getPaginationRowModel: getPaginationRowModel() } : {}),
    enableColumnPinning: true,
    enableSorting,
    getSubRows,
    getRowCanExpand,
    ...(getRowId != null ? { getRowId } : {}),
    ...(enableRowSelection != null ? { enableRowSelection } : {}),
    state: {
      get sorting() {
        return sorting.value;
      },
      get pagination() {
        return paginationState.value;
      },
      get rowSelection() {
        return rowSelection.value;
      },
      get expanded() {
        return expanded.value;
      },
      get columnVisibility() {
        return columnUi.value.columnVisibility ?? {};
      },
      get columnOrder() {
        const next = columnUi.value.columnOrder;
        return next ?? [];
      },
    },
    onSortingChange(updater) {
      sorting.value = applyUpdater(updater, sorting.value);
    },
    onPaginationChange(updater) {
      paginationState.value = applyUpdater(updater, paginationState.value);
    },
    onRowSelectionChange(updater) {
      rowSelection.value = applyUpdater(updater, rowSelection.value);
    },
    onExpandedChange(updater) {
      expanded.value = applyUpdater(updater, expanded.value);
    },
    onColumnVisibilityChange(updater) {
      const prev = columnUi.value.columnVisibility ?? {};
      const next = applyUpdater(updater, prev);
      columnUi.value = { ...columnUi.value, columnVisibility: next };
    },
    onColumnOrderChange(updater) {
      const prev = columnUi.value.columnOrder ?? [];
      const next = applyUpdater(updater, prev);
      columnUi.value = { ...columnUi.value, columnOrder: next };
    },
  });

  watch(
    () => toValue(data),
    () => {
      if (enablePagination && paginationState.value.pageIndex > 0) {
        const maxPage = Math.max(0, Math.ceil(toValue(data).length / paginationState.value.pageSize) - 1);
        if (paginationState.value.pageIndex > maxPage) {
          paginationState.value = { ...paginationState.value, pageIndex: 0 };
        }
      }
    },
  );

  return { table, isServerMode: false };
}
